import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { featuredInventoryApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const FeaturedInventoryPage: React.FC = () => {
  const { data: vehicles, isLoading, error } = useQuery(
    'featured-inventory-available',
    () => featuredInventoryApi.getFeaturedInventory('available')
  );

  // Filter for available vehicles only
  const availableVehicles = (vehicles as any[])?.filter((v: any) => v.is_available && !v.is_sold) || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Грешка при зареждане</h2>
          <p className="text-gray-600">Моля, опитайте отново по-късно.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Налични автомобили</h1>
          <p className="text-gray-600">
            Автомобили, които са на склад и готови за доставка
          </p>
        </div>

        {/* Vehicle Grid */}
        {availableVehicles.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">🚗</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Няма налични автомобили в момента
            </h3>
            <p className="text-gray-600 mb-6">
              Разгледайте нашите аукционни автомобили или се свържете с нас за повече информация.
            </p>
            <Link
              to="/vehicles"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Разгледай аукционите
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {availableVehicles.map((vehicle: any) => (
              <Link
                key={vehicle.id}
                to={`/featured-inventory/${vehicle.id}`}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] bg-gray-200">
                  {vehicle.images && vehicle.images.length > 0 ? (
                    <img
                      src={vehicle.images[0].image_url}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-gray-400 text-4xl">🚗</span>
                    </div>
                  )}
                  
                  {/* Available Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-500 text-white shadow-lg">
                      ✓ Наличен
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  
                  {vehicle.title && (
                    <p className="text-sm text-gray-600 mb-2">{vehicle.title}</p>
                  )}

                  {/* Specs */}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
                    {vehicle.engine && (
                      <span className="bg-gray-100 px-2 py-1 rounded">{vehicle.engine}</span>
                    )}
                    {vehicle.transmission && (
                      <span className="bg-gray-100 px-2 py-1 rounded">{vehicle.transmission}</span>
                    )}
                    {vehicle.odometer && (
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        {vehicle.odometer.toLocaleString()} {vehicle.odometer_unit || 'км'}
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="border-t pt-3">
                    <p className="text-2xl font-bold text-green-600">
                      {Math.round(vehicle.price * 1.96).toLocaleString()} лв
                      <span className="text-sm font-normal text-gray-500 ml-2">
                        ({Math.round(vehicle.price).toLocaleString()} €)
                      </span>
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedInventoryPage;

